// Card "Vendedores" da ficha do cliente (specs/22): principal + os que também atendem (até 4).
import { MAX_VENDEDORES_ADICIONAIS } from '@crm/contracts';
import { useState } from 'react';
import { api, mensagemErro } from '../api';
import { Erro, Icon } from '../components/ui';
import { abertasQueMudam, type NegociacaoCliente } from '../negociacao';
import { AvisoTransferencia } from './CardNegociacoes';
import { useUsuarios } from './Clientes';

export interface VendedorAdicional { id: string; nome: string; ativo: boolean }
interface Linha { usuarioId: string; principal: boolean }

const iniciais = (n: string) => n.split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

export function CardVendedores({ cliente, podeEditar, onSalvo, negociacoes = [] }: {
  cliente: { id: string; vendedorId: string | null; vendedorNome: string | null; vendedoresAdicionais: VendedorAdicional[] };
  podeEditar: boolean;
  onSalvo: () => Promise<unknown>;
  /** Para avisar o que muda de dono ao salvar (specs/27) */
  negociacoes?: NegociacaoCliente[];
}) {
  const usuarios = useUsuarios(podeEditar);
  const [linhas, setLinhas] = useState<Linha[] | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const ativos = (usuarios.data ?? []).filter((u) => u.status === 'ATIVO');
  const nomeDe = (id: string) => usuarios.data?.find((u) => u.id === id)?.nome ?? '—';

  function editar() {
    const l: Linha[] = [];
    if (cliente.vendedorId) l.push({ usuarioId: cliente.vendedorId, principal: true });
    for (const v of cliente.vendedoresAdicionais) l.push({ usuarioId: v.id, principal: false });
    setLinhas(l);
    setErro(null);
  }
  const muda = (i: number, p: Partial<Linha>) => setLinhas((ls) => ls!.map((x, j) => (j === i ? { ...x, ...p } : x)));

  // validação de tela (o servidor valida de novo)
  const ids = (linhas ?? []).map((l) => l.usuarioId);
  const repetido = ids.find((id, i) => ids.indexOf(id) !== i);
  const semPrincipal = (linhas ?? []).length > 0 && !(linhas ?? []).some((l) => l.principal);
  const aviso = repetido ? `${nomeDe(repetido)} aparece duas vezes.` : semPrincipal ? 'Escolha o vendedor principal.' : null;
  const max = MAX_VENDEDORES_ADICIONAIS + 1;
  const principalNovo = (linhas ?? []).find((l) => l.principal)?.usuarioId ?? null;
  const vaoJunto = linhas ? abertasQueMudam(negociacoes, principalNovo, linhas.filter((l) => !l.principal).map((l) => l.usuarioId)) : [];

  async function salvar() {
    if (!linhas || aviso) return;
    setSalvando(true);
    setErro(null);
    try {
      const principal = linhas.find((l) => l.principal)?.usuarioId ?? null;
      await api(`/clientes/${cliente.id}`, {
        method: 'PATCH',
        body: { vendedorId: principal, vendedoresAdicionais: linhas.filter((l) => !l.principal).map((l) => l.usuarioId) },
      });
      await onSalvo();
      setLinhas(null);
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="card" aria-labelledby="h-vendedores">
      <div className="card-head">
        <h2 id="h-vendedores">Vendedores</h2>
        {podeEditar && !linhas && <button className="btn sm" style={{ marginLeft: 'auto' }} onClick={editar}>Editar</button>}
      </div>

      {!linhas ? (
        <>
          {!cliente.vendedorId && <span className="small muted">Sem vendedor (fila de distribuição).</span>}
          <ul className="vend-lista">
            {cliente.vendedorId && (
              <li><span className="av" aria-hidden="true">{iniciais(cliente.vendedorNome ?? '?')}</span>
                <span className="col" style={{ gap: 0, minWidth: 0 }}><span>{cliente.vendedorNome}</span><span className="small faint">Dono da carteira</span></span>
                <span className="badge blue" style={{ marginLeft: 'auto' }}>Principal</span></li>
            )}
            {cliente.vendedoresAdicionais.map((v) => (
              <li key={v.id}><span className="av" aria-hidden="true">{iniciais(v.nome)}</span>
                <span className="col" style={{ gap: 0, minWidth: 0 }}><span>{v.nome}</span><span className="small faint">Também atende</span></span>
                {!v.ativo && <span className="badge" style={{ marginLeft: 'auto' }}>inativo</span>}</li>
            ))}
          </ul>
          {cliente.vendedoresAdicionais.length > 0 && (
            <span className="small faint">Todos veem a ficha e os contatos. Cada negociação é de um vendedor: no quadro aparecem só as dele; aqui na ficha as dos colegas aparecem para consulta.</span>
          )}
        </>
      ) : (
        <>
          <div className="vend-edit vend-edit-head"><span className="small faint">Principal</span><span className="small faint">Vendedor</span><span /></div>
          {linhas.map((l, i) => (
            <div key={i} className="vend-edit">
              <input type="radio" name={`principal-${cliente.id}`} aria-label={`Principal: ${nomeDe(l.usuarioId)}`} checked={l.principal}
                onChange={() => setLinhas((ls) => ls!.map((x, j) => ({ ...x, principal: j === i })))} />
              <select className="select" aria-label="Vendedor" value={l.usuarioId} onChange={(e) => muda(i, { usuarioId: e.target.value })}
                style={repetido === l.usuarioId ? { borderColor: 'var(--red-border)' } : undefined}>
                {/* mantém na lista quem está gravado mesmo se inativo */}
                {!ativos.some((u) => u.id === l.usuarioId) && <option value={l.usuarioId}>{nomeDe(l.usuarioId)} (inativo)</option>}
                {ativos.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
              <button type="button" className="btn sm" aria-label={`Remover ${nomeDe(l.usuarioId)}`}
                onClick={() => setLinhas((ls) => {
                  const resto = ls!.filter((_, j) => j !== i);
                  if (l.principal && resto[0]) resto[0] = { ...resto[0], principal: true };
                  return resto;
                })}><Icon name="fechar" size={14} /></button>
            </div>
          ))}
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <button type="button" className="btn sm" disabled={linhas.length >= max || !ativos.length}
              onClick={() => {
                const livre = ativos.find((u) => !ids.includes(u.id)) ?? ativos[0]!;
                setLinhas((ls) => [...ls!, { usuarioId: livre.id, principal: ls!.length === 0 }]);
              }}>+ Adicionar vendedor</button>
            <span className="small faint">{linhas.length} de {max}</span>
          </div>
          {aviso && <span className="small" role="alert" style={{ color: 'var(--red-text)' }}>{aviso}</span>}
          {!aviso && principalNovo && <AvisoTransferencia negs={vaoJunto} para={nomeDe(principalNovo)} />}
          <Erro erro={erro} />
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn" onClick={() => setLinhas(null)} disabled={salvando}>Cancelar</button>
            <button className="btn primary" onClick={() => void salvar()} disabled={salvando || !!aviso}>{salvando ? 'Salvando…' : 'Salvar'}</button>
          </div>
        </>
      )}
    </section>
  );
}
